import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Zap, MapPin, Calculator, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SetupModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    farmName: '',
    location: '',
    landSize: '',
    landUnit: 'acres',
    residueType: '',
    estimatedWaste: '',
    additionalInfo: ''
  });

  const residueTypes = [
    'Rice Straw',
    'Wheat Straw',
    'Corn Stalks',
    'Sugarcane Bagasse',
    'Cotton Stalks',
    'Other Agricultural Waste'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: 'Login Required',
        description: 'Please login or create an account to continue',
        variant: 'destructive'
      });
      onClose();
      navigate('/login');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/farm-setup`, formData);
      toast({
        title: 'Success!',
        description: 'Your farm setup is complete. We\'ll contact you shortly.',
      });
      handleClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit setup',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFormData({
      farmName: '',
      location: '',
      landSize: '',
      landUnit: 'acres',
      residueType: '',
      estimatedWaste: '',
      additionalInfo: ''
    });
    onClose();
  };

  const nextStep = () => {
    if (step === 1 && (!formData.farmName || !formData.location)) {
      toast({
        title: 'Required Fields',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }
    if (step === 2 && (!formData.landSize || !formData.residueType)) {
      toast({
        title: 'Required Fields',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Zap className="w-6 h-6 text-neo-emerald" />
            3-Minute Farm Setup
          </DialogTitle>
          <DialogDescription>
            Step {step} of 3 - Complete your farm registration
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mb-4">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-2 flex-1 rounded-full transition-colors ${
                s <= step ? 'bg-neo-emerald' : 'bg-neo-gray/20'
              }`}
            ></div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-neo-emerald mb-4">
                <MapPin className="w-5 h-5" />
                <h3 className="font-semibold text-lg text-gray-900">Farm Information</h3>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="farmName">Farm Name *</Label>
                <Input
                  id="farmName"
                  placeholder="Enter your farm name"
                  value={formData.farmName}
                  onChange={(e) => setFormData({ ...formData, farmName: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Location/Address *</Label>
                <Textarea
                  id="location"
                  placeholder="Enter farm location with village, district, state"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  rows={3}
                />
              </div>

              <div className="bg-neo-emerald/10 border border-neo-emerald/20 rounded-lg p-4">
                <p className="text-sm text-neo-gray font-body">
                  <CheckCircle2 className="w-4 h-4 inline mr-1 text-neo-emerald" />
                  GPS coordinates will be automatically captured during field verification
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-neo-emerald mb-4">
                <Calculator className="w-5 h-5" />
                <h3 className="font-semibold text-lg text-gray-900">Farm Details</h3>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="landSize">Land Size *</Label>
                <div className="flex gap-2">
                  <Input
                    id="landSize"
                    type="number"
                    step="0.1"
                    placeholder="Enter land size"
                    value={formData.landSize}
                    onChange={(e) => setFormData({ ...formData, landSize: e.target.value })}
                    required
                    className="flex-1"
                  />
                  <Select 
                    value={formData.landUnit} 
                    onValueChange={(value) => setFormData({ ...formData, landUnit: value })}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="acres">Acres</SelectItem>
                      <SelectItem value="hectares">Hectares</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="residueType">Primary Residue Type *</Label>
                <Select 
                  value={formData.residueType} 
                  onValueChange={(value) => setFormData({ ...formData, residueType: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select residue type" />
                  </SelectTrigger>
                  <SelectContent>
                    {residueTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="estimatedWaste">Estimated Annual Waste (tons)</Label>
                <Input
                  id="estimatedWaste"
                  type="number"
                  step="0.1"
                  placeholder="Approximate annual agricultural waste"
                  value={formData.estimatedWaste}
                  onChange={(e) => setFormData({ ...formData, estimatedWaste: e.target.value })}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-neo-emerald mb-4">
                <CheckCircle2 className="w-5 h-5" />
                <h3 className="font-semibold text-lg text-gray-900">Additional Information</h3>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="additionalInfo">Any Additional Information</Label>
                <Textarea
                  id="additionalInfo"
                  placeholder="Share any specific requirements, questions, or concerns..."
                  value={formData.additionalInfo}
                  onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="bg-gradient-to-br from-neo-emerald/10 to-neo-emerald/5 border border-neo-emerald/20 rounded-xl p-6">
                <h4 className="font-heading font-semibold text-neo-black mb-3">What happens next?</h4>
                <ul className="space-y-2 text-sm text-neo-gray font-body">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-neo-emerald mt-0.5 flex-shrink-0" />
                    <span>Our team will contact you within 24 hours</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-neo-emerald mt-0.5 flex-shrink-0" />
                    <span>Field verification and GPS mapping</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-neo-emerald mt-0.5 flex-shrink-0" />
                    <span>Biochar production setup guidance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-neo-emerald mt-0.5 flex-shrink-0" />
                    <span>Start earning from carbon credits</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            {step > 1 && (
              <Button type="button" variant="outline" onClick={prevStep}>
                Previous
              </Button>
            )}
            {step < 3 ? (
              <Button type="button" className="flex-1 bg-neo-emerald hover:bg-[#1db868] text-neo-white glow-emerald-hover" onClick={nextStep}>
                Next Step
              </Button>
            ) : (
              <Button type="submit" className="flex-1 bg-neo-emerald hover:bg-[#1db868] text-neo-white glow-emerald-hover" disabled={isLoading}>
                {isLoading ? 'Submitting...' : 'Complete Setup'}
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SetupModal;
